import type { SurveyBuilderManager } from '@/state/surveys/useSurveyBuilder';
import type { Survey } from 'types';
import { cn } from 'helpers/cn';
import { Input } from '@/components/ui/input';
import { SURVEY_DEFAULT_TEXTS, SURVEY_POSITIONS } from 'definitions';

type StepThemeProps = { manager: SurveyBuilderManager };

const FIELD_INPUT = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

type TextKey = keyof typeof SURVEY_DEFAULT_TEXTS;

// Limits mirror the API's textsSchema.
const TEXT_FIELDS: { key: TextKey; label: string; max: number }[] = [
  { key: 'next', label: 'Next button', max: 40 },
  { key: 'submit', label: 'Submit button', max: 40 },
  { key: 'thankYou', label: 'Thank-you message', max: 200 },
  { key: 'closeLabel', label: 'Close button, for screen readers', max: 60 },
];

export const StepTheme = ({ manager }: StepThemeProps) => {
  const { draft, updateDraft } = manager;
  // End-user data, not console chrome: this hex is the customer's own widget accent
  // and lands in a stylesheet on their page, so it is never tokenised here.
  const accent = draft.theme.accentColor ?? '#2563eb';
  const isValidAccent = /^#[0-9a-fA-F]{6}$/.test(accent);

  const setPosition = (position: Survey['position']) => () => updateDraft((state) => ({ ...state, position }));

  const setAccent = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = ev.target;
    updateDraft((state) => ({ ...state, theme: { ...state.theme, accentColor: value } }));
  };

  const setRadius = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(ev.target.value);
    updateDraft((state) => ({ ...state, theme: { ...state.theme, borderRadius: value } }));
  };

  // An emptied field is left out, so the widget's default shows instead and the API
  // never sees an empty string.
  const setText = (key: TextKey) => (ev: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = ev.target;
    updateDraft((state) => {
      const { [key]: _dropped, ...rest } = state.texts;
      return { ...state, texts: value.trim() ? { ...rest, [key]: value } : rest };
    });
  };

  const setCap = (field: 'cooldownDays' | 'maxPrompts') => (ev: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(ev.target.value);
    updateDraft((state) => ({ ...state, frequencyCap: { ...state.frequencyCap, [field]: value } }));
  };

  return (
    <div className="space-y-5">
      <h2 className="text-foreground m-0 text-lede font-medium">Appearance, frequency and texts</h2>

      <div className="space-y-2">
        <span className="micro-label block">Position</span>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {SURVEY_POSITIONS.map((position: Survey['position']) => (
            <button
              key={position}
              type="button"
              onClick={setPosition(position)}
              className={cn(
                'micro-label rounded-lg border px-3 py-2 transition-colors duration-150',
                draft.position === position
                  ? 'bg-sel-bg border-sel-line text-foreground'
                  : 'border-line bg-panel2 text-dim hover:bg-hover hover:text-foreground',
              )}
            >
              {position}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="theme-accent" className="micro-label block">
            Accent colour
          </label>
          <div className="flex items-center gap-2">
            <input
              id="theme-accent-picker"
              type="color"
              value={isValidAccent ? accent : '#2563eb'}
              onChange={setAccent}
              className="border-line bg-field h-9 w-12 cursor-pointer rounded-lg border p-1"
              aria-label="Pick accent colour"
            />
            <Input id="theme-accent" value={accent} onChange={setAccent} maxLength={7} className={FIELD_INPUT} />
          </div>
          {/* Rejected by the API too: this value lands in a stylesheet on the customer's page. */}
          {!isValidAccent && <p className="text-danger m-0 text-micro">Must be a 6-digit hex value, e.g. #2563eb.</p>}
        </div>

        <div className="space-y-1.5">
          <label htmlFor="theme-radius" className="micro-label block">
            Corner radius (px)
          </label>
          <Input
            id="theme-radius"
            type="number"
            min={0}
            max={32}
            value={draft.theme.borderRadius ?? 12}
            onChange={setRadius}
            className={FIELD_INPUT}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor="cap-cooldown" className="micro-label block">
            Days before re-asking after a dismissal
          </label>
          <Input
            id="cap-cooldown"
            type="number"
            min={0}
            max={365}
            value={draft.frequencyCap.cooldownDays}
            onChange={setCap('cooldownDays')}
            className={FIELD_INPUT}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="cap-max" className="micro-label block">
            Maximum prompts per visitor
          </label>
          <Input
            id="cap-max"
            type="number"
            min={1}
            max={10}
            value={draft.frequencyCap.maxPrompts}
            onChange={setCap('maxPrompts')}
            className={FIELD_INPUT}
          />
        </div>
      </div>

      <p className="supporting m-0">
        Someone who answers is never asked again. These limits apply to visitors who dismiss without answering.
      </p>

      <div className="space-y-2">
        <div className="space-y-1">
          <span className="micro-label block">Widget texts</span>
          <p className="supporting m-0">
            Write them in your site's language. Leave a field empty to keep the English shown in grey.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {TEXT_FIELDS.map(({ key, label, max }) => (
            <div key={key} className="space-y-1.5">
              <label htmlFor={`survey-text-${key}`} className="micro-label block">
                {label}
              </label>
              <Input
                id={`survey-text-${key}`}
                value={draft.texts[key] ?? ''}
                onChange={setText(key)}
                placeholder={SURVEY_DEFAULT_TEXTS[key]}
                maxLength={max}
                className={FIELD_INPUT}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
